import PropTypes from 'prop-types'
import {StyledTabs, StyledTab} from '../FilterTabsAndLayoutStyles'
import {StyledTabLabelContainer, StyledTabBadge} from './FilterCategoryTabsStyles'
import {useFilters} from '../../../store/hooks/filters'

/**
 * FilterCategoryTabs Component
 *
 * Horizontal scrollable tabs for filter categories.
 * Shows "All" tab plus individual category tabs with count badges.
 *
 * @param {Object} props
 * @param {number} props.value - Current active tab index
 * @param {Function} props.onChange - Callback when tab changes
 * @param {Array} props.categories - Array of category objects from config
 * @param {Object} props.categoryFilterCounts - Map of categoryId to selected filter count
 */
const FilterCategoryTabs = ({value, onChange, categories, categoryFilterCounts}) => {
  const {filterRef} = useFilters()
  const handleChange = (event, newValue) => {
    onChange(newValue)
  }

  const getTotalFilterCount = () => {
    return Object.values(categoryFilterCounts).reduce((sum, count) => sum + count, 0)
  }

  const renderTabLabel = (label, count, tabIndex) => {
    const isActive = value === tabIndex
    return (
      <StyledTabLabelContainer>
        {label}
        {count > 0 && <StyledTabBadge isActive={isActive}>{count}</StyledTabBadge>}
      </StyledTabLabelContainer>
    )
  }

  const totalCount = getTotalFilterCount()

  return (
    <StyledTabs
      value={value}
      onChange={handleChange}
      variant='scrollable'
      scrollButtons='auto'
      allowScrollButtonsMobile
      aria-label='Filter category tabs'
      ref={filterRef}
    >
      <StyledTab label={renderTabLabel('All', totalCount, 0)} id='filter-tab-all' aria-controls='filter-tabpanel-all' />
      {categories.map((category, index) => {
        const count = categoryFilterCounts[category.id] || 0
        const tabIndex = index + 1
        // Use customName if available (custom name from backend), fallback to title
        const categoryLabel = category.customName || category.title
        return (
          <StyledTab
            key={category.id}
            label={renderTabLabel(categoryLabel, count, tabIndex)}
            id={`filter-tab-${category.id}`}
            aria-controls={`filter-tabpanel-${category.id}`}
          />
        )
      })}
    </StyledTabs>
  )
}

FilterCategoryTabs.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired
    })
  ).isRequired,
  categoryFilterCounts: PropTypes.object.isRequired
}

export default FilterCategoryTabs
